import React from 'react';
import { useEffect, useState, useMemo } from 'react';
import {
  SplittingRoutingPartnerInfoListRequest,
  useFetchSplittingRoutingPartnerInfoQuery,
} from '../splittingroutingpartnerinfo-api';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Breadcrumb,
  Card,
  Select,
  Space,
  Table,
  Spin,
  Row,
  Col,
  Tooltip,
  Input,
  Button,
  message
} from 'antd';
import {
  HomeOutlined,
  DownloadOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { SplittingRoutingPartnerInfoViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';
import * as XLSX from 'xlsx';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';
import { ConvertUTCtoCDT } from '../../../core/utils';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function SplittingRoutingPartnerInfoScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();

  // Map frontend column keys to backend column names (camelCase to PascalCase)
  const getBackendColumnName = (columnKey: string): string => {
    const columnMapping: Record<string, string> = {
      'partnerId': 'PartnerId',
      'partnerName': 'PartnerName',
      'carrierId': 'PartnerId',
      'identifierType': 'IdentifierType',
      'identifierValue': 'IdentifierValue',
      'source': 'Source',
      'target': 'Target',
      'version': 'Version',
      'standard': 'Standard',
      'testFlag': 'TestFlag',
      'documentType': 'DocumentType',
      'transactionType': 'TransactionType',
      'customer': 'Customer',
      'profileMap': 'ProfileMap',
      'routingIdentifierType': 'RoutingIdentifierType',
      'routingIdentifierValue': 'RoutingIdentifierValue',
      'folderPath': 'FolderPath',
      'lastTransactionDate': 'LastTransactionDate',
    };
    return columnMapping[columnKey] || columnKey.charAt(0).toUpperCase() + columnKey.slice(1);
  };

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const currentSortColumn = query.get('sortColumn');
    const currentSortDirection = query.get('sortDirection');

    // Map frontend column key to backend column name to compare
    const backendColumnName = getBackendColumnName(columnKey);

    if (currentSortColumn === backendColumnName) {
      return currentSortDirection === 'DESC' ? 'descend' : 'ascend';
    }
    return null;
  };

  const splittingRoutingPartnerInfoTable: TableState = {
    columns: [
      {
        title: 'Customer Name',
        dataIndex: '',
        key: 'customer',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <Tooltip title={item.customer || ''}>
              <span>{item.customer || ''}</span>
            </Tooltip>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('customer'),
        fixed: 'left',
      },
      {
        title: 'Carrier Name',
        dataIndex: '',
        key: 'partnerName',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <Tooltip title={item.partnerName || ''}>
              <span>{item.partnerName || ''}</span>
            </Tooltip>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('partnerName'),
      },
      {
        title: 'Carrier ID',
        dataIndex: '',
        key: 'partnerId',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <Space>
              <span>{item.partnerId}</span>
            </Space>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('partnerId'),
      },
      {
        title: 'Identifier Type',
        dataIndex: '',
        key: 'identifierType',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.identifierType || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('identifierType'),
      },
      {
        title: 'Identifier Value',
        dataIndex: '',
        key: 'identifierValue',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.identifierValue || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('identifierValue'),
      },
      {
        title: 'Source',
        dataIndex: '',
        key: 'source',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.source || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('source'),
      },
      {
        title: 'Target',
        dataIndex: '',
        key: 'target',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.target || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('target'),
      },
      {
        title: 'Document Type',
        dataIndex: '',
        key: 'documentType',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.documentType || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('documentType'),
      },
      {
        title: 'Transaction Type',
        dataIndex: '',
        key: 'transactionType',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.transactionType || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('transactionType'),
      },
      {
        title: 'Version',
        dataIndex: '',
        key: 'version',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.version || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('version'),
      },
      {
        title: 'Standard',
        dataIndex: '',
        key: 'standard',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.standard || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('standard'),
      },
      {
        title: 'Test Flag',
        dataIndex: '',
        key: 'testFlag',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.testFlag || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('testFlag'),
      },
      {
        title: 'Routing Identifier Type',
        dataIndex: '',
        key: 'routingIdentifierType',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.routingIdentifierType || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('routingIdentifierType'),
      },
      {
        title: 'Routing Identifier Value',
        dataIndex: '',
        key: 'routingIdentifierValue',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{item.routingIdentifierValue || ''}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('routingIdentifierValue'),
      },
      {
        title: 'Profile Map',
        dataIndex: '',
        key: 'profileMap',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <Tooltip title={item.profileMap || ''}>
              <span>{item.profileMap || ''}</span>
            </Tooltip>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('profileMap'),
      },
      {
        title: 'FTP Outbound Path',
        dataIndex: '',
        key: 'folderPath',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <Tooltip title={item.folderPath || ''}>
              <span>{item.folderPath || ''}</span>
            </Tooltip>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('folderPath'),
      },
      {
        title: 'Last Transaction Date',
        dataIndex: '',
        key: 'lastTransactionDate',
        render: (item: SplittingRoutingPartnerInfoViewModel) => (
          <>
            <span>{ConvertUTCtoCDT(item.lastTransactionDate || '')}</span>
          </>
        ),
        sorter: (a, b) => 0,  // Disable client-side sorting, use server-side
        sortOrder: getSortOrderForColumn('lastTransactionDate'),
      },
    ],
  };

  const getCurrentState = (): SplittingRoutingPartnerInfoListRequest => {
    const filterStr = query.get('q') || '';
    // Convert filter string to JSON format for FilterJson parameter
    // Format: partnerId:*value* AND partnerName:*value* AND source:*value* -> JSON object
    const filterObj: any = {};
    const partnerIdMatch = filterStr.match(/partnerId:\*([^*]*)\*/);
    const partnerNameMatch = filterStr.match(/partnerName:\*([^*]*)\*/);
    const sourceMatch = filterStr.match(/source:\*([^*]*)\*/);
    const targetMatch = filterStr.match(/target:\*([^*]*)\*/);
    const profileMapMatch = filterStr.match(/profileMap:\*([^*]*)\*/);
    const routingIdentifierTypeMatch = filterStr.match(/routingIdentifierType:\*([^*]*)\*/);
    const routingIdentifierValueMatch = filterStr.match(/routingIdentifierValue:\*([^*]*)\*/);
    const transactionTypeMatch = filterStr.match(/transactionType:\*([^*]*)\*/);
    const customerMatch = filterStr.match(/customer:\*([^*]*)\*/);
    
    if (partnerIdMatch && partnerIdMatch[1]) filterObj.partnerId = partnerIdMatch[1];
    if (partnerNameMatch && partnerNameMatch[1]) filterObj.partnerName = partnerNameMatch[1];
    if (sourceMatch && sourceMatch[1]) filterObj.source = sourceMatch[1];
    if (targetMatch && targetMatch[1]) filterObj.target = targetMatch[1];
    if (profileMapMatch && profileMapMatch[1]) filterObj.profileMap = profileMapMatch[1];
    if (routingIdentifierTypeMatch && routingIdentifierTypeMatch[1]) filterObj.routingIdentifierType = routingIdentifierTypeMatch[1];
    if (routingIdentifierValueMatch && routingIdentifierValueMatch[1]) filterObj.routingIdentifierValue = routingIdentifierValueMatch[1];
    if (transactionTypeMatch && transactionTypeMatch[1]) filterObj.transactionType = transactionTypeMatch[1];
    if (customerMatch && customerMatch[1]) filterObj.customer = customerMatch[1];
    
    const filterJson = Object.keys(filterObj).length > 0 ? JSON.stringify(filterObj) : null;
    
    return {
      current: Number(query.get('p')) || DefaultPage,
      pageSize: Number(query.get('size')) || DefaultPageSize,
      filter: filterStr,
      sortBy: query.get('sortBy'),
      sortColumn: query.get('sortColumn') || 'PartnerName',
      sortDirection: query.get('sortDirection') || 'ASC',
      searchText: (() => {
        const searchTextValue = query.get('searchText');
        return (searchTextValue && searchTextValue.trim() && searchTextValue !== 'null') ? searchTextValue : undefined;
      })(),
      filterJson: filterJson,
    };
  };

  const [searchText, setSearchText] = useState<string>('');
  const [carrierId, setCarrierId] = useState<string>('');
  const [partnerName, setPartnerName] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [target, setTarget] = useState<string>('');
  const [profileMap, setProfileMap] = useState<string>('');
  const [routingIdentifierType, setRoutingIdentifierType] = useState<string>('');
  const [routingIdentifierValue, setRoutingIdentifierValue] = useState<string>('');
  const [transactionType, setTransactionType] = useState<string>('');
  const [customer, setCustomer] = useState<string>('');
  const [downLoading, setDownLoading] = useState<boolean>(false);
  const token = useSelector((state: RootState) => state.auth.token);

  // Memoize pagination to ensure stable reference and proper refetching when URL changes
  const currentPagination = useMemo(() => getCurrentState(), [location.search, params.tenantId]);

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<SplittingRoutingPartnerInfoViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchSplittingRoutingPartnerInfoQuery(currentPagination, {
    refetchOnMountOrArgChange: true,
    skip: false,
  });

  // Debug: Log when data changes
  useEffect(() => {
    if (pagedData?.results) {
      console.log('SplittingRoutingPartnerInfo data updated:', {
        totalCount: pagedData.totalCount,
        resultsCount: pagedData.results.length,
        filterJson: currentPagination.filterJson,
        results: pagedData.results.map(r => ({ partnerId: r.partnerId, partnerName: r.partnerName }))
      });
    }
  }, [pagedData, currentPagination.filterJson]);


  useEffect(() => {
    // Extract filter values from query string
    const filterStr = query.get('q') || '';
    const searchTextParam = query.get('searchText');
    // Handle case where searchText might be the string "null" or "undefined" or actually null/undefined
    const searchTextValue = (!searchTextParam || searchTextParam === 'null' || searchTextParam === 'undefined') ? '' : searchTextParam;
    // Parse filter string to extract individual filter values
    // Format: partnerId:*value* AND partnerName:*value* AND source:*value* etc.
    const partnerIdMatch = filterStr.match(/partnerId:\*([^*]*)\*/);
    const partnerNameMatch = filterStr.match(/partnerName:\*([^*]*)\*/);
    const sourceMatch = filterStr.match(/source:\*([^*]*)\*/);
    const targetMatch = filterStr.match(/target:\*([^*]*)\*/);
    const profileMapMatch = filterStr.match(/profileMap:\*([^*]*)\*/);
    const routingIdentifierTypeMatch = filterStr.match(/routingIdentifierType:\*([^*]*)\*/);
    const routingIdentifierValueMatch = filterStr.match(/routingIdentifierValue:\*([^*]*)\*/);
    const transactionTypeMatch = filterStr.match(/transactionType:\*([^*]*)\*/);
    const customerMatch = filterStr.match(/customer:\*([^*]*)\*/);
    
    setSearchText(searchTextValue);
    if (partnerIdMatch) setCarrierId(partnerIdMatch[1]);
    else setCarrierId('');
    if (partnerNameMatch) setPartnerName(partnerNameMatch[1]);
    else setPartnerName('');
    if (sourceMatch) setSource(sourceMatch[1]);
    else setSource('');
    if (targetMatch) setTarget(targetMatch[1]);
    else setTarget('');
    if (profileMapMatch) setProfileMap(profileMapMatch[1]);
    else setProfileMap('');
    if (routingIdentifierTypeMatch) setRoutingIdentifierType(routingIdentifierTypeMatch[1]);
    else setRoutingIdentifierType('');
    if (routingIdentifierValueMatch) setRoutingIdentifierValue(routingIdentifierValueMatch[1]);
    else setRoutingIdentifierValue('');
    if (transactionTypeMatch) setTransactionType(transactionTypeMatch[1]);
    else setTransactionType('');
    if (customerMatch) setCustomer(customerMatch[1]);
    else setCustomer('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, params.tenantId]);

  const handlePageSizeChange = (current: number, size: number | string) => {
    // Handle page size change - convert string to number if needed
    let pageSize: number = DefaultPageSize;
    if (typeof size === 'string') {
      // Parse string numbers (including "10000")
      const parsed = parseInt(size, 10);
      if (!isNaN(parsed)) {
        pageSize = parsed;
      }
    } else if (typeof size === 'number') {
      pageSize = size;
    }
    
    const queryParams: any = {};
    // Copy all existing query params
    query.forEach((val, key) => {
      if (key !== 'p' && key !== 'size') {
        queryParams[key] = val;
      }
    });
    
    queryParams.p = 1; // Reset to first page when page size changes
    queryParams.size = pageSize;
    queryParams.q = currentPagination.filter || '';
    
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    }
    
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<SplittingRoutingPartnerInfoViewModel> | SorterResult<SplittingRoutingPartnerInfoViewModel>[],
    extra: TableCurrentDataSource<SplittingRoutingPartnerInfoViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<SplittingRoutingPartnerInfoViewModel>;

    // Build new sort parameters - preserve current values by default
    let newSortColumn = currentPagination.sortColumn || 'PartnerName';
    let newSortDirection = currentPagination.sortDirection || 'ASC';
    let newPage = paginationConfig.current || 1;

    // If sorting changed, update sort parameters and reset to page 1
    if (columnKey && order) {
      const backendColumnName = getBackendColumnName(columnKey.toString());
      newSortColumn = backendColumnName;
      newSortDirection = order === 'ascend' ? 'ASC' : 'DESC';
      newPage = 1;  // Reset to page 1 on sort change
    }

    // Use the pageSize from config, defaulting to current if not provided
    const pageSize = paginationConfig.pageSize || currentPagination.pageSize;

    // If page size changed, reset to page 1
    if (pageSize !== currentPagination.pageSize) {
      newPage = 1;
    }

    const queryParams: any = {
      sortColumn: newSortColumn,
      sortDirection: newSortDirection,
      p: newPage,
      size: pageSize,
      q: currentPagination.filter || '',
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: string }
  ) => (
    option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const getCurrentFilterValues = () => {
    const filterStr = query.get('q') || '';
    const partnerIdMatch = filterStr.match(/partnerId:\*([^*]*)\*/);
    const partnerNameMatch = filterStr.match(/partnerName:\*([^*]*)\*/);
    const sourceMatch = filterStr.match(/source:\*([^*]*)\*/);
    const targetMatch = filterStr.match(/target:\*([^*]*)\*/);
    const profileMapMatch = filterStr.match(/profileMap:\*([^*]*)\*/);
    const routingIdentifierTypeMatch = filterStr.match(/routingIdentifierType:\*([^*]*)\*/);
    const routingIdentifierValueMatch = filterStr.match(/routingIdentifierValue:\*([^*]*)\*/);
    const transactionTypeMatch = filterStr.match(/transactionType:\*([^*]*)\*/);
    const customerMatch = filterStr.match(/customer:\*([^*]*)\*/);
    
    return {
      partnerId: partnerIdMatch ? partnerIdMatch[1] : '',
      partnerName: partnerNameMatch ? partnerNameMatch[1] : '',
      source: sourceMatch ? sourceMatch[1] : '',
      target: targetMatch ? targetMatch[1] : '',
      profileMap: profileMapMatch ? profileMapMatch[1] : '',
      routingIdentifierType: routingIdentifierTypeMatch ? routingIdentifierTypeMatch[1] : '',
      routingIdentifierValue: routingIdentifierValueMatch ? routingIdentifierValueMatch[1] : '',
      transactionType: transactionTypeMatch ? transactionTypeMatch[1] : '',
      customer: customerMatch ? customerMatch[1] : '',
    };
  };

  const buildFilterQuery = (updatedPartnerId?: string, updatedPartnerName?: string, updatedSource?: string, updatedTarget?: string, updatedProfileMap?: string, updatedRoutingIdentifierType?: string, updatedRoutingIdentifierValue?: string, updatedTransactionType?: string, updatedCustomer?: string) => {
    const currentFilters = getCurrentFilterValues();
    const filters: string[] = [];
    
    const pid = updatedPartnerId !== undefined ? updatedPartnerId : currentFilters.partnerId;
    const pname = updatedPartnerName !== undefined ? updatedPartnerName : currentFilters.partnerName;
    const src = updatedSource !== undefined ? updatedSource : currentFilters.source;
    const tgt = updatedTarget !== undefined ? updatedTarget : currentFilters.target;
    const profileMap = updatedProfileMap !== undefined ? updatedProfileMap : currentFilters.profileMap;
    const routingIdType = updatedRoutingIdentifierType !== undefined ? updatedRoutingIdentifierType : currentFilters.routingIdentifierType;
    const routingIdValue = updatedRoutingIdentifierValue !== undefined ? updatedRoutingIdentifierValue : currentFilters.routingIdentifierValue;
    const transType = updatedTransactionType !== undefined ? updatedTransactionType : currentFilters.transactionType;
    const customer = updatedCustomer !== undefined ? updatedCustomer : currentFilters.customer;
    
    if (pid) filters.push(`partnerId:*${pid}*`);
    if (pname) filters.push(`partnerName:*${pname}*`);
    if (src) filters.push(`source:*${src}*`);
    if (tgt) filters.push(`target:*${tgt}*`);
    if (profileMap) filters.push(`profileMap:*${profileMap}*`);
    if (routingIdType) filters.push(`routingIdentifierType:*${routingIdType}*`);
    if (routingIdValue) filters.push(`routingIdentifierValue:*${routingIdValue}*`);
    if (transType) filters.push(`transactionType:*${transType}*`);
    if (customer) filters.push(`customer:*${customer}*`);
    return filters.join(' AND ');
  };

  const handleSearchTextChange = (value: string) => {
    const filterQuery = buildFilterQuery();
    // Build query params object, excluding searchText from the spread
    const queryParams: any = {};
    // Copy all query params except searchText
    query.forEach((val, key) => {
      if (key !== 'searchText') {
        queryParams[key] = val;
      }
    });
    // Add standard params - preserve current page size
    queryParams.p = DefaultPage;
    queryParams.size = currentPagination.pageSize;
    queryParams.q = filterQuery;
    // Only add searchText if it has a value
    if (value && value.trim()) {
      queryParams.searchText = value;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleCarrierIdChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      // Remove searchText from query params if it's empty or invalid
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handlePartnerNameChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      // Remove searchText from query params if it's empty or invalid
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleSourceChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      // Remove searchText from query params if it's empty or invalid
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleTargetChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, undefined, undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      // Remove searchText from query params if it's empty or invalid
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleProfileMapChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, undefined, undefined, undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      // Remove searchText from query params if it's empty or invalid
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleRoutingIdentifierTypeChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, undefined, undefined, undefined, undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleRoutingIdentifierValueChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, undefined, undefined, undefined, undefined, undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleTransactionTypeChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, undefined, undefined, undefined, undefined, undefined, undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      // Remove searchText from query params if it's empty or invalid
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleCustomerChange = (value: string | null | undefined) => {
    const filterQuery = buildFilterQuery(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, value || '');
    const queryParams: any = {
      ...query,
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: filterQuery,
    };
    const currentSearchText = query.get('searchText');
    if (currentSearchText && currentSearchText.trim() && currentSearchText !== 'null' && currentSearchText !== 'undefined') {
      queryParams.searchText = currentSearchText;
    } else {
      // Remove searchText from query params if it's empty or invalid
      delete queryParams.searchText;
    }
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  const handleClearAllFilters = () => {
    const queryParams: any = {
      p: DefaultPage,
      size: currentPagination.pageSize,
      q: '',
    };
    // Preserve sort parameters if they exist
    const sortBy = query.get('sortBy');
    const sortColumn = query.get('sortColumn');
    const sortDirection = query.get('sortDirection');
    if (sortBy) queryParams.sortBy = sortBy;
    if (sortColumn) queryParams.sortColumn = sortColumn;
    if (sortDirection) queryParams.sortDirection = sortDirection;
    
    navigate(
      getLink(
        'SplittingRoutingPartnerInfoList',
        { ...params },
        queryParams
      )
    );
  };

  // Get unique values for filter dropdowns from the data
  const getUniqueValues = (key: keyof SplittingRoutingPartnerInfoViewModel): string[] => {
    if (!pagedData.results) return [];
    const values = pagedData.results
      .map(item => item[key])
      .filter((value): value is string => value !== null && value !== undefined && value !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return values;
  };

  // Download all records as Excel based on current filters
  const onExport = async () => {
    try {
      setDownLoading(true);
      
      // Get current filter state
      const currentState = getCurrentState();
      const totalCount = pagedData?.totalCount || 0;
      
      if (totalCount === 0) {
        message.warning('No data available to export');
        setDownLoading(false);
        return;
      }

      // Build API URL with filters and pageSize = totalCount to get all records
      const params = new URLSearchParams();
      params.append('pageNumber', '1');
      params.append('pageSize', totalCount.toString());
      if (currentState.sortColumn) params.append('sortColumn', currentState.sortColumn);
      if (currentState.sortDirection) params.append('sortDirection', currentState.sortDirection);
      if (currentState.searchText) params.append('searchText', currentState.searchText);
      if (currentState.filterJson) {
        params.append('filterJson', currentState.filterJson);
      }
      if (currentState.filter) params.append('filter', currentState.filter);
      if (currentState.sortBy) params.append('sortBy', currentState.sortBy);

      const baseUrl = process.env.REACT_APP_BASE_API_URL || '';
      const url = `${baseUrl}/RoutingRules/splitting-routing-partner-info?${params.toString()}`;

      // Make API call to get all records
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PaginatedResult<SplittingRoutingPartnerInfoViewModel> = await response.json();
      
      if (!data.results || data.results.length === 0) {
        message.warning('No data available to export');
        setDownLoading(false);
        return;
      }

      // Convert to Excel
      const worksheet = XLSX.utils.json_to_sheet(data.results);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Splitting Routing Rule");
      XLSX.writeFile(workbook, `SplittingRoutingRule_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      message.success(`Successfully exported ${data.results.length} records`);
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export data. Please try again.');
    } finally {
      setDownLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Customer Carrier Information
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
                <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
                Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="Customer Carrier Information">
              <Link to={getLink('SplittingRoutingPartnerInfoList', { tenantId: params.tenantId })}>Customer Carrier Information</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={
          <Space>
            <Button htmlType="button" onClick={onExport} loading={downLoading} icon={<DownloadOutlined />}>
              Download as Excel
            </Button>
          </Space>
        }
      >
        <Card>
          <style>{`
            .ant-select-selection-item {
              white-space: normal !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
              overflow-wrap: break-word !important;
              line-height: 1.5 !important;
              max-width: 100% !important;
              display: block !important;
            }
            .ant-select-selector {
              min-height: auto !important;
              height: auto !important;
              padding: 4px 11px !important;
            }
            .ant-select-selection-search-input {
              white-space: normal !important;
              word-wrap: break-word !important;
            }
            .ant-select-selection-placeholder {
              white-space: normal !important;
            }
            .ant-select-selection-overflow {
              flex-wrap: wrap !important;
            }
            .ant-select-selection-item-content {
              white-space: normal !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
              overflow-wrap: break-word !important;
              display: inline-block !important;
              max-width: 100% !important;
            }
            .ant-select-item-option-content {
              white-space: normal !important;
              word-wrap: break-word !important;
              word-break: break-word !important;
            }
          `}</style>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Search
                </div>
                <Input
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  placeholder="Search..."
                  value={searchText || ''}
                  onChange={(e) => handleSearchTextChange(e.target.value)}
                  allowClear
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Customer Name
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={customer || undefined}
                  onChange={handleCustomerChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('customer').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Carrier Name
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={partnerName || undefined}
                  onChange={handlePartnerNameChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('partnerName').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Carrier ID
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={carrierId || undefined}
                  onChange={handleCarrierIdChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('partnerId').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Source
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={source || undefined}
                  onChange={handleSourceChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('source').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Target
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={target || undefined}
                  onChange={handleTargetChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('target').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Transaction Type
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={transactionType || undefined}
                  onChange={handleTransactionTypeChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('transactionType').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Profile Map
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={profileMap || undefined}
                  onChange={handleProfileMapChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('profileMap').map((item: any) => ({ label: item, value: item }))}
                  dropdownStyle={{ maxWidth: '500px' }}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Routing Identifier Type
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={routingIdentifierType || undefined}
                  onChange={handleRoutingIdentifierTypeChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('routingIdentifierType').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
            <Col className="gutter-row" span={8}>
              <Space size="large" style={{ width: '100%' }}>
                <div style={{minWidth: 150, flexShrink: 0}}>
                  Routing Identifier Value
                </div>
                <Select
                  style={{minWidth: 200, maxWidth: '100%', width: '100%'}}
                  showSearch
                  placeholder="Search..."
                  optionFilterProp="children"
                  filterOption={filterOption}
                  value={routingIdentifierValue || undefined}
                  onChange={handleRoutingIdentifierValueChange}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '')
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  allowClear={true}
                  loading={isFetching}
                  options={getUniqueValues('routingIdentifierValue').map((item: any) => ({ label: item, value: item }))}
                />
              </Space>
            </Col>
          </Row>
          <Row style={{ marginTop: 16 }}>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Button 
                htmlType="button" 
                onClick={handleClearAllFilters} 
                icon={<ClearOutlined />}
                danger
              >
                Clear All Filters
              </Button>
            </Col>
          </Row>
        </Card>

        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Table
              key={`table-${JSON.stringify(currentPagination.filterJson || currentPagination.filter || 'default')}-${currentPagination.current}-${pagedData.totalCount || 0}`}
              scroll={{ x: true }}
              dataSource={pagedData?.results || []}
              onChange={handleTableChange}
              rowKey={(record, index) => `${record.partnerId}-${record.partnerName}-${record.source}-${record.target}-${record.transactionType}-${record.documentType}-${record.version}-${record.standard}-${record.testFlag}-${record.profileMap}-${record.routingIdentifierType}-${record.routingIdentifierValue}-${record.customer}-${record.identifierType}-${record.identifierValue}-${record.folderPath}-${index}`}
              pagination={{
                current: currentPagination.current,
                pageSize: currentPagination.pageSize,
                total: pagedData?.totalCount || 0,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50", "100", "10000"],
                showSizeChanger: true,
                onShowSizeChange: handlePageSizeChange,
                locale: { items_per_page: " / Page" }
              }}
              columns={splittingRoutingPartnerInfoTable.columns}
              loading={isFetching}
            />
          </Space>
        </Card>
      </PageHeader>
    </div>
  );
}

